import { PencilIcon, PlusIcon, PlusSmallIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { ProfileCard } from "@psyfiers-it/ui-lib";
import { isEmpty } from "lodash";

import { ArrowBackIcon } from "../../../../../asssets/icons";
import {
  createNewProfileType,
  getBadgeUIConfig,
  getServiceDetails,
  getSystemAdminUserGroups,
  updateProfileType,
  updateBadgeUIConfig,
  uploadFileToAWS,
  getUploadingFileData
} from "../../../../../service";
import ToggleComponent from "../../../../common/toggleComponent";
import { Button, Input } from "../../../../form";
import { BaseModal } from "../../../../popups";
import PersonalInformationFormEditor from "./personalInformation";
import ProfileCustomDataEditor from "./profileCustomDataEditor";
import VideoEditor from "./videoEditor";
import { ColorPicker } from "../../../../common";
import InputSelect from "../../../../form/InputSelect";

// Constants for caption field configuration
const MAX_CAPTION_FIELDS = 4;
const MAX_PROFESSION_SUB_FIELDS = 1;
const ALLOWED_FIELD_TYPES = {
  WORD_SKILLS_PROFESSION: "worldSkillsProfession",
  PARTICIPANT_LOCATION: "participantLocation",
  CURRENT_JOB_NAME: "currentJobName"
};

// Field type labels for UI
const FIELD_TYPE_LABELS = {
  [ALLOWED_FIELD_TYPES.WORD_SKILLS_PROFESSION]: "WorldSkills Profession",
  [ALLOWED_FIELD_TYPES.PARTICIPANT_LOCATION]: "Participant Location",
  [ALLOWED_FIELD_TYPES.CURRENT_JOB_NAME]: "Current Job Name"
};

// Simple profession sub field options
const PROFESSION_SUB_FIELD_OPTIONS = [{ label: "Dome Text", value: "domeText", _id: "domeText" }];

// Helper function to filter fields by allowed types
const filterAllowedFields = (fields) => {
  return fields.filter((field) => {
    if (!field || !field.value) return false;

    // Check if field type/value matches exactly with allowed types
    const fieldType = field.fieldType || field.type || field.value;
    return Object.values(ALLOWED_FIELD_TYPES).includes(fieldType);
  });
};

const validationTypeOptions = [
  { label: "Auto", value: "auto" },
  { label: "Manual", value: "manual" }
];

export default function Profiles({ title, org, currentService, setShowComponent }) {
  const [profileName, setProfileName] = useState("");
  const [showNewProfileModal, setShowNewProfileModal] = useState(false);
  const [isNewProfileSubmitting, setIsNewProfileSubmitting] = useState(false);
  const [profileSubmittingId, setProfileSubmittingId] = useState(null);
  const [profileList, setProfilesList] = useState([]);
  const [editProfileNameId, setEditProfileNameId] = useState(null);
  const [isVideoEditorModalOpen, setIsVideoEditorModalOpen] = useState(false);
  const [isPersonalInfoEditorModalOpen, setIsPersonalInfoEditorModalOpen] = useState(false);
  const [isCustomDataEditorModalOpen, setIsCustomDataEditorModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [systemAdminGroups, setSystemAdminGroups] = useState([]);
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [validationMethod, setValidationMethod] = useState(null);
  const [organizationBadgeUIConfig, setOrganizationBadgeUIConfig] = useState({});
  const [organizationInputFields, setOrganizationInputFields] = useState([]);
  const [isOrganizationSubmitting, setIsOrganizationSubmitting] = useState(false);

  const fetchAllProfiles = async () => {
    try {
      const { data } = await getServiceDetails(org._id, currentService._id);
      const fetchedProfilesList =
        data.profiles.map((profile) => {
          const badgeUIConfig = { ...profile.badgeUIConfig?.config };

          // Reconstruct captionFieldKeys object from stored _id
          if (badgeUIConfig.captionFieldKeys && profile.inputFields) {
            if (Array.isArray(badgeUIConfig.captionFieldKeys)) {
              // Handle array of field IDs (multi-select)
              const reconstructedFields = badgeUIConfig.captionFieldKeys
                .map((fieldId) => {
                  const fieldData = profile.inputFields.find(
                    (inputField) => inputField.field && inputField.field._id === fieldId
                  );

                  if (fieldData) {
                    return {
                      label: fieldData.field.name,
                      value: fieldData.field.key,
                      _id: fieldData.field._id
                    };
                  }
                  return null;
                })
                .filter(Boolean); // Remove null values

              badgeUIConfig.captionFieldKeys =
                reconstructedFields.length > 0 ? reconstructedFields : null;
            } else {
              // Handle single field ID (backward compatibility)
              const fieldData = profile.inputFields.find(
                (inputField) =>
                  inputField.field && inputField.field._id === badgeUIConfig.captionFieldKeys
              );

              if (fieldData) {
                badgeUIConfig.captionFieldKeys = [
                  {
                    label: fieldData.field.name,
                    value: fieldData.field.key,
                    _id: fieldData.field._id
                  }
                ];
              } else {
                badgeUIConfig.captionFieldKeys = null;
              }
            }
          }

          // professionSubFields are stored as values directly, no reconstruction needed

          return {
            ...profile,
            badgeUIConfig
          };
        }) || [];

      setProfilesList(fetchedProfilesList);
    } catch (error) {
      toast.error("Failed to load profiles types.");
      console.error("Failed to load profiles types:", error);
    }
  };

  const fetchAllSystemAdminGroups = async () => {
    try {
      const { data: userGroupsData } = await getSystemAdminUserGroups(org._id);
      const temp = [];
      userGroupsData.forEach((group) => {
        if (!group?.groups) return;

        group.groups.forEach((subGroup) => {
          temp.push({ label: `${group.name} | ${subGroup.name}`, value: subGroup._id });
        });
      });
      setSystemAdminGroups(temp);
    } catch (error) {
      toast.error("Failed to load system admin groups.");
      console.error("Failed to load system admin groups:", error);
    }
  };

  useEffect(() => {
    fetchAllProfiles();
    fetchAllSystemAdminGroups();
  }, [org]);

  const handleNewProfileCreate = async (e) => {
    e.preventDefault();

    try {
      setIsNewProfileSubmitting(true);
      await createNewProfileType(org._id, {
        name: profileName,
        serviceId: currentService._id
      });
      toast.success("New profile type created successfully.");
      setShowNewProfileModal(false);
      setProfileName("");
      fetchAllProfiles();
    } catch (error) {
      toast.error("Failed to create new profile type.");
      console.error("Failed to create new profile type: ", error);
    } finally {
      setIsNewProfileSubmitting(false);
    }
  };

  const handleFeatureToggle = (profileIndex, feature) => {
    const updatedProfilesList = JSON.parse(JSON.stringify(profileList));
    updatedProfilesList[profileIndex][feature] = !updatedProfilesList[profileIndex][feature];
    setProfilesList(updatedProfilesList);
  };

  const handleProfileTypeUpdate = async (profile) => {
    try {
      if (profileSubmittingId) {
        return;
      }
      if (
        (!isEmpty(profile.contactEmail) &&
          !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.contactEmail)) ||
        /[!#$%^&*(),?":{}|<>]/.test(profile.contactEmail)
      ) {
        toast.error("Invalid admin email address");
        return;
      }

      // Validate contact email address if provided
      if (
        profile.contactEmailAddress &&
        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.contactEmailAddress)
      ) {
        toast.error("Invalid contact email address");
        return;
      }

      let updatedProfileName = profileName?.trim() === "" ? profile.name : profileName;

      const {
        badgeUIConfig: { cornerImageObj, captionFieldKeys, professionSubFields, ...badgeUIConfig },
        ...profileRemaining
      } = profile;

      let secondaryImage = badgeUIConfig?.secondaryImage;
      if (cornerImageObj) {
        console.log("organizationBadgeUIConfig", cornerImageObj);
        secondaryImage = await uploadSecondaryImage(cornerImageObj, profileRemaining._id);
      }

      // Prepare badgeUIConfig for database - only send _id for captionFieldKeys and professionSubFields
      const badgeUIConfigForDB = {
        ...badgeUIConfig,
        secondaryImage,
        captionFieldKeys: Array.isArray(captionFieldKeys)
          ? captionFieldKeys
              .filter((field) => field !== null && field !== undefined)
              .map((field) => field._id)
          : null,
        professionSubFields: Array.isArray(professionSubFields)
          ? professionSubFields.filter((field) => field !== null && field !== undefined)
          : null
      };

      setProfileSubmittingId(profileRemaining._id);
      await updateProfileType(org._id, {
        ...profileRemaining,
        name: updatedProfileName,
        badgeUIConfig: badgeUIConfigForDB
      });
      toast.success("Profile information updated successfully.");
      setProfileName("");
      setEditProfileNameId(null);
      fetchAllProfiles();
    } catch (error) {
      toast.error("Failed to update profile information.");
      console.error("Failed to update profile information: ", error);
    } finally {
      setProfileSubmittingId(null);
    }
  };

  const handleEditProfileNameClick = (profile) => {
    setProfileName(profile.name);
    setEditProfileNameId(profile._id);
  };

  const showVideoEditorModal = (profile) => {
    setSelectedProfile(profile);
    setIsVideoEditorModalOpen(!isVideoEditorModalOpen);
  };

  const updateBadgeUI = (event, profileIndex, name, subname, isOrganization) => {
    let badgeUIConfig = {};
    let updatedProfilesList = {};
    if (isOrganization) {
      badgeUIConfig = JSON.parse(JSON.stringify(organizationBadgeUIConfig));
    } else {
      updatedProfilesList = JSON.parse(JSON.stringify(profileList));
      badgeUIConfig = profileList[profileIndex].badgeUIConfig || { ringGradientColor: {} };
    }

    if (subname) {
      if (!badgeUIConfig[name]) badgeUIConfig[name] = {};
      badgeUIConfig[name][subname] = event.hex;
    } else {
      badgeUIConfig[name] = event.hex;
    }

    if (isOrganization) {
      setOrganizationBadgeUIConfig({ ...badgeUIConfig });
      return;
    } else {
      updatedProfilesList[profileIndex].badgeUIConfig = badgeUIConfig;
      setProfilesList(updatedProfilesList);
    }
  };

  const updateCaptionFieldConfig = (configName, value, profileIndex, isOrganization) => {
    if (isOrganization) {
      const updatedConfig = JSON.parse(JSON.stringify(organizationBadgeUIConfig));
      updatedConfig[configName] = value;
      setOrganizationBadgeUIConfig(updatedConfig);
    } else {
      const updatedProfilesList = JSON.parse(JSON.stringify(profileList));
      if (!updatedProfilesList[profileIndex].badgeUIConfig) {
        updatedProfilesList[profileIndex].badgeUIConfig = {};
      }
      // Handle array of selected fields (dynamic dropdowns)
      if (value === null || (Array.isArray(value) && value.length === 0)) {
        updatedProfilesList[profileIndex].badgeUIConfig[configName] = null;
      } else if (Array.isArray(value)) {
        // Filter out null/undefined values but keep the array structure for UI
        const filteredValue = value.filter((field) => field !== null && field !== undefined);
        updatedProfilesList[profileIndex].badgeUIConfig[configName] = value; // Keep original for UI state
      } else {
        updatedProfilesList[profileIndex].badgeUIConfig[configName] = value;
      }
      setProfilesList(updatedProfilesList);
    }
  };

  const getCurrentBadgeUI = async (organizationId) => {
    try {
      const { data } = await getBadgeUIConfig(organizationId);
      const badgeUI = data?.badgeUIConfig || {};

      // First, collect all input fields from all profiles in this service
      let allInputFields = [];
      if (profileList.length > 0) {
        profileList.forEach((profile) => {
          if (profile.inputFields) {
            profile.inputFields.forEach((inputField) => {
              if (
                inputField.field &&
                !allInputFields.find((field) => field._id === inputField.field._id)
              ) {
                allInputFields.push({
                  label: inputField.field.name,
                  value: inputField.field.key,
                  _id: inputField.field._id
                });
              }
            });
          }
        });
        setOrganizationInputFields(allInputFields);
      } else {
        setOrganizationInputFields([]);
      }

      // Now reconstruct captionFieldKeys object from stored _id for organization using all available fields
      if (badgeUI.captionFieldKeys && allInputFields.length > 0) {
        if (Array.isArray(badgeUI.captionFieldKeys)) {
          // Handle array of field IDs (multi-select)
          const reconstructedFields = badgeUI.captionFieldKeys
            .map((fieldId) => {
              const fieldData = allInputFields.find((field) => field._id === fieldId);

              if (fieldData) {
                return {
                  label: fieldData.label,
                  value: fieldData.value,
                  _id: fieldData._id
                };
              }
              return null;
            })
            .filter(Boolean); // Remove null values

          badgeUI.captionFieldKeys = reconstructedFields.length > 0 ? reconstructedFields : null;
        } else {
          // Handle single field ID (backward compatibility)
          const fieldData = allInputFields.find((field) => field._id === badgeUI.captionFieldKeys);

          if (fieldData) {
            badgeUI.captionFieldKeys = [
              {
                label: fieldData.label,
                value: fieldData.value,
                _id: fieldData._id
              }
            ];
          } else {
            badgeUI.captionFieldKeys = null;
          }
        }
      }

      setOrganizationBadgeUIConfig(badgeUI);
    } catch (error) {
      toast.error("Failed to get badge UI");
      console.error("Failed to get badge UI: ", error);
    }
  };

  useEffect(() => {
    if (currentOrganization) {
      getCurrentBadgeUI(currentOrganization.value);
    }
  }, [currentOrganization, profileList]);

  const uploadSecondaryImage = async (image, organisation) => {
    try {
      const fileName = "badge-ui" + "/" + organisation + "/" + image.name;
      const {
        data: { url }
      } = await getUploadingFileData({
        name: fileName,
        type: image.type
      });
      const resp = await uploadFileToAWS(url, image, image.type);
      console.log("resp", resp);
      return fileName;
    } catch (error) {
      console.error("Failed to upload secondary image: ", error);
      toast.error("Failed to upload secondary image");
    }
  };

  const updateCurrentBadgeUI = async () => {
    try {
      if (!currentOrganization) {
        toast.error("Please select a group");
        return;
      }

      setIsOrganizationSubmitting(true);

      const { cornerImageObj, captionFieldKeys, professionSubFields, ...badgeUIConfig } =
        organizationBadgeUIConfig;

      let secondaryImage = badgeUIConfig?.secondaryImage;
      if (cornerImageObj) {
        console.log("organizationBadgeUIConfig", cornerImageObj);
        secondaryImage = await uploadSecondaryImage(cornerImageObj, currentOrganization.value);
      }

      // Prepare badgeUIConfig for database - only send _id for captionFieldKeys and professionSubFields
      const badgeUIConfigForDB = {
        ...badgeUIConfig,
        secondaryImage,
        captionFieldKeys: Array.isArray(captionFieldKeys)
          ? captionFieldKeys
              .filter((field) => field !== null && field !== undefined)
              .map((field) => field._id)
          : captionFieldKeys?._id || null,
        professionSubFields: Array.isArray(professionSubFields)
          ? professionSubFields.filter((field) => field !== null && field !== undefined)
          : professionSubFields || null
      };

      await updateBadgeUIConfig(currentOrganization.value, {
        badgeUIConfig: badgeUIConfigForDB
      });
      toast.success("Badge UI updated successfully.");
    } catch (error) {
      toast.error("Failed to update badge UI");
      console.error("Failed to update badge UI: ", error);
    } finally {
      setIsOrganizationSubmitting(false);
    }
  };

  const handleImageSelect = (file, index, isOrganization, isRemoval) => {
    if (isOrganization) {
      const currentObject = JSON.parse(JSON.stringify(organizationBadgeUIConfig));
      currentObject.cornerImageObj = file;
      if (isRemoval) currentObject.secondaryImage = null;
      setOrganizationBadgeUIConfig(currentObject);
    } else {
      const profileListTemp = [...profileList];
      profileListTemp[index].badgeUIConfig.cornerImageObj = file;
      if (isRemoval) profileListTemp[index].badgeUIConfig.secondaryImage = null;

      setProfilesList([...profileListTemp]);
    }
  };

  return (
    <>
      <button
        className="flex gap-3 items-center w-[fit-content] flex-wrap mb-10"
        onClick={setShowComponent}>
        <span className="w-8 h-8">
          <ArrowBackIcon />
        </span>
        <h2 className="text-xl leading-10 font-bold">{title ?? "Profiles"}</h2>
      </button>

      <div className="w-full pl-8">
        {profileList.map((profile, profileIndex) => (
          <div key={profile._id} className="border-2 rounded p-4 mb-8">
            <div className="flex justify-between">
              <div className="mt-5">
                {editProfileNameId === profile._id ? (
                  <Input
                    name="edit-name"
                    onChange={(e) => setProfileName(e.target.value)}
                    value={profileName || ""}
                  />
                ) : (
                  <h2 className="text-xl font-bold">{profile.name}</h2>
                )}
              </div>
              <div className="mt-5 flex items-center">
                {editProfileNameId !== profile._id && (
                  <PencilIcon
                    className="w-[1rem] h-[1rem] mr-2 cursor-pointer"
                    onClick={() => handleEditProfileNameClick(profile)}
                  />
                )}
                <ToggleComponent
                  handleCompToggle={() => handleFeatureToggle(profileIndex, "isActive")}
                  element="is-active"
                  isToggle={profile.isActive}
                />
              </div>
            </div>
            <div className="mt-10">
              <h2 className="font-semibold mt-5">Profile Setup</h2>
              <div className="flex justify-between">
                <h2 className="mt-5">Personal Information Form</h2>
                <div className="mt-5 cursor-pointer flex items-center">
                  <PencilIcon
                    className="w-[1rem] h-[1rem] mr-2"
                    onClick={() => {
                      setSelectedProfile(profile);
                      setIsPersonalInfoEditorModalOpen(true);
                    }}
                  />
                  <ToggleComponent
                    handleCompToggle={() => handleFeatureToggle(profileIndex, "editableForm")}
                    element="is-active"
                    isToggle={profile.editableForm}
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <Input
                  label={"Email admin on validation request"}
                  placeholder={"sender@gmail.com"}
                  type="email"
                  onChange={(e) => {
                    const updatedProfiles = [...profileList];
                    updatedProfiles[profileIndex].contactEmail = e.target.value;
                    setProfilesList(updatedProfiles);
                  }}
                  value={profile.contactEmail}
                />
                <div className="mt-5 cursor-pointer flex items-center">
                  <ToggleComponent
                    handleCompToggle={() =>
                      handleFeatureToggle(profileIndex, "sendValidationEmail")
                    }
                    isToggle={profile.sendValidationEmail}
                    id="validation-admin-email"
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <h2 className="mt-5">Custom Data</h2>
                <div className="mt-5 cursor-pointer flex items-center">
                  <PencilIcon
                    className="w-[1rem] h-[1rem] mr-2"
                    onClick={() => {
                      setSelectedProfile(profile);
                      setIsCustomDataEditorModalOpen(true);
                    }}
                  />
                </div>
              </div>

              <div className="flex justify-between">
                <h2 className="mt-5">Video Function</h2>
                <div className="mt-5 flex items-center">
                  <PencilIcon
                    className="w-[1rem] h-[1rem] mr-2 cursor-pointer"
                    onClick={() => showVideoEditorModal(profile)}
                  />
                  <ToggleComponent
                    handleCompToggle={() => handleFeatureToggle(profileIndex, "hasVideoFunction")}
                    element="video-function"
                    isToggle={profile.hasVideoFunction}
                  />
                </div>
              </div>
            </div>
            <div className="mt-10">
              <div className="flex justify-between">
                <h2 className="font-semibold">Profile Webpage</h2>
                <ToggleComponent
                  handleCompToggle={() => handleFeatureToggle(profileIndex, "hasProfileWebPage")}
                  element="profile-webpage"
                  isToggle={profile.hasProfileWebPage}
                />
              </div>
            </div>

            <div className="mt-10">
              <div className="flex flex-col my-2">
                <h2 className="">Validation Type</h2>

                <InputSelect
                  onChange={(e) => {
                    const updatedProfiles = [...profileList];
                    updatedProfiles[profileIndex].validationMethod = e.value;
                    setProfilesList(updatedProfiles);
                  }}
                  value={validationTypeOptions.find(
                    (option) => option.value === profile.validationMethod
                  )}
                  subheading={"Select Validation Type"}
                  className="w-4/6"
                  options={validationTypeOptions}
                />
              </div>
            </div>
            <div className="mt-10">
              <div className="flex justify-between">
                <h2 className="font-semibold">Discovery Page</h2>
                <ToggleComponent
                  handleCompToggle={() => handleFeatureToggle(profileIndex, "hasDiscoveryWebPage")}
                  element="discovery-webpage"
                  isToggle={profile.hasDiscoveryWebPage}
                />
              </div>
            </div>

            <div className="mt-10">
              <div className="flex justify-between gap-4 flex-col">
                <Input
                  className="w-full"
                  label={"Profile URL Structure"}
                  type="text"
                  onChange={(e) => {
                    const updatedProfiles = [...profileList];
                    updatedProfiles[profileIndex].profileUrlStructure = e.target.value;
                    setProfilesList(updatedProfiles);
                  }}
                  value={profile.profileUrlStructure || ""}
                />
                <Input
                  className="w-full"
                  label={"PDF URL Structure"}
                  type="text"
                  onChange={(e) => {
                    const updatedProfiles = [...profileList];
                    updatedProfiles[profileIndex].pdfUrlStructure = e.target.value;
                    setProfilesList(updatedProfiles);
                  }}
                  value={profile.pdfUrlStructure || ""}
                />
              </div>
            </div>

            <div className="mt-10">
              <h2 className="font-semibold mb-4">Contact Information</h2>
              <div className="flex justify-between gap-4 flex-col">
                <Input
                  className="w-full"
                  label={"Phone Number"}
                  type="tel"
                  placeholder="+1234567890"
                  onChange={(e) => {
                    const updatedProfiles = [...profileList];
                    updatedProfiles[profileIndex].contactPhone = e.target.value;
                    setProfilesList(updatedProfiles);
                  }}
                  value={profile.contactPhone || ""}
                />
                <Input
                  className="w-full"
                  label={"Email Address"}
                  type="email"
                  placeholder="contact@example.com"
                  onChange={(e) => {
                    const updatedProfiles = [...profileList];
                    updatedProfiles[profileIndex].contactEmailAddress = e.target.value;
                    setProfilesList(updatedProfiles);
                  }}
                  value={profile.contactEmailAddress || ""}
                />
                <div className="mt-2">
                  <label className="block text-sm font-medium mb-2">Phone Type</label>
                  <div className="flex gap-6">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={`phoneType-${profileIndex}`}
                        value="whatsapp"
                        checked={profile.contactPhoneType === "whatsapp"}
                        onChange={(e) => {
                          const updatedProfiles = [...profileList];
                          updatedProfiles[profileIndex].contactPhoneType = e.target.value;
                          setProfilesList(updatedProfiles);
                        }}
                        className="mr-2"
                      />
                      <span>WhatsApp</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={`phoneType-${profileIndex}`}
                        value="phone"
                        checked={profile.contactPhoneType === "phone"}
                        onChange={(e) => {
                          const updatedProfiles = [...profileList];
                          updatedProfiles[profileIndex].contactPhoneType = e.target.value;
                          setProfilesList(updatedProfiles);
                        }}
                        className="mr-2"
                      />
                      <span>Phone</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={`phoneType-${profileIndex}`}
                        value="both"
                        checked={profile.contactPhoneType === "both"}
                        onChange={(e) => {
                          const updatedProfiles = [...profileList];
                          updatedProfiles[profileIndex].contactPhoneType = e.target.value;
                          setProfilesList(updatedProfiles);
                        }}
                        className="mr-2"
                      />
                      <span>Both</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <BadgeUI
              updateBadgeUI={updateBadgeUI}
              profile={profile}
              profileIndex={profileIndex}
              handleImageSelect={handleImageSelect}
              updateCaptionFieldConfig={updateCaptionFieldConfig}
              availableFields={
                profile.inputFields?.length > 0
                  ? filterAllowedFields(
                      profile.inputFields?.map((field) => {
                        return {
                          label: field.field?.name,
                          value: field.field?.key,
                          _id: field.field?._id
                        };
                      }) || []
                    )
                  : []
              }
            />

            <div className="flex mt-8 justify-end">
              <Button.AdminPanelButton
                type="submit"
                isSave={true}
                loading={profile._id === profileSubmittingId}
                bgColor="#FC9E5A"
                textColor="#FFFFFF"
                color="white"
                onClick={() => handleProfileTypeUpdate(profile)}>
                Save
              </Button.AdminPanelButton>
            </div>
          </div>
        ))}

        <div className="border-2 rounded p-4 mb-8">
          <div className="flex justify-between">
            <div className="mt-5">
              <h2 className="font-bold mt-5">Group-Specific Badge UI</h2>
            </div>
          </div>
          <div className="mt-10">
            <h2 className="font-semibold mt-5">(S.A) Profile Group</h2>
            <InputSelect
              onChange={setCurrentOrganization}
              value={currentOrganization}
              subheading={"Select Group"}
              className="w-4/6"
              options={systemAdminGroups}
            />
          </div>

          <BadgeUI
            updateBadgeUI={updateBadgeUI}
            profile={{ badgeUIConfig: organizationBadgeUIConfig }}
            isOrganization={true}
            profileIndex={0}
            handleImageSelect={handleImageSelect}
            updateCaptionFieldConfig={updateCaptionFieldConfig}
            availableFields={filterAllowedFields(organizationInputFields)}
          />

          <div className="flex mt-8 justify-end">
            <Button.AdminPanelButton
              type="submit"
              isSave={true}
              loading={isOrganizationSubmitting}
              bgColor="#FC9E5A"
              textColor="#FFFFFF"
              color="white"
              onClick={updateCurrentBadgeUI}>
              Save
            </Button.AdminPanelButton>
          </div>
        </div>

        {/* New Profile Type Modal */}
        <BaseModal open={showNewProfileModal} setOpen={setShowNewProfileModal} title={"Enter Name"}>
          <form onSubmit={handleNewProfileCreate}>
            <Input
              label="Title"
              name="name"
              onChange={(e) => setProfileName(e.target.value)}
              value={profileName || ""}
            />
            <div className="mt-5">
              <Button.AdminPanelButton
                type="submit"
                isSave={true}
                loading={isNewProfileSubmitting}
                bgColor="#FC9E5A"
                textColor="#FFFFFF"
                color="white">
                Create
              </Button.AdminPanelButton>
            </div>
          </form>
        </BaseModal>
        {/* New Profile Type Add Button - Triggers Modal */}
        <div
          className="mt-10 cursor-pointer inline-block"
          onClick={() => {
            setShowNewProfileModal(!showNewProfileModal);
          }}>
          <PlusIcon className={`w-7 h-7 stroke-2 stroke-white bg-[#E3E3E3] rounded-full`} />
        </div>
      </div>

      {/* Video Editor */}
      <BaseModal
        open={isVideoEditorModalOpen}
        setOpen={setIsVideoEditorModalOpen}
        title={"Video Editor"}>
        <VideoEditor org={org} profile={selectedProfile} />
      </BaseModal>

      {/* Personal Information Form Editor */}
      <BaseModal
        open={isPersonalInfoEditorModalOpen}
        setOpen={setIsPersonalInfoEditorModalOpen}
        title={"Personal Information Form Editor"}>
        <PersonalInformationFormEditor
          org={org}
          profile={selectedProfile}
          onSubmit={() => {
            fetchAllProfiles();
            setIsPersonalInfoEditorModalOpen(false);
          }}
        />
      </BaseModal>

      {/* Custom Data Editor */}
      <BaseModal
        open={isCustomDataEditorModalOpen}
        setOpen={setIsCustomDataEditorModalOpen}
        title={"Custom Data Editor"}>
        <ProfileCustomDataEditor
          org={org}
          profile={selectedProfile}
          onSubmit={() => {
            fetchAllProfiles();
            setIsCustomDataEditorModalOpen(false);
          }}
        />
      </BaseModal>
    </>
  );
}

const BadgeUI = ({
  profile,
  updateBadgeUI,
  profileIndex,
  isOrganization,
  handleImageSelect,
  updateCaptionFieldConfig = null,
  availableFields = []
}) => {
  return (
    <>
      <div className="mt-10 mb-2">
        <div className="flex justify-between">
          <h2 className="font-semibold">Profile Badge UI</h2>
        </div>
      </div>

      <div className="flex justify-between my-2">
        <h2 className="">background Colour</h2>
        <ColorPicker
          color={profile.badgeUIConfig?.primaryColor}
          handleChange={(event) =>
            updateBadgeUI(event, profileIndex, "primaryColor", null, isOrganization)
          }
        />
      </div>
      <div className="flex justify-between my-2">
        <h2 className="">Ring Colour</h2>
        <ColorPicker
          color={profile.badgeUIConfig?.secondaryColor}
          handleChange={(event) =>
            updateBadgeUI(event, profileIndex, "secondaryColor", null, isOrganization)
          }
        />
      </div>
      <div className="flex justify-between my-2">
        <h2 className="">Title Colour</h2>
        <ColorPicker
          color={profile.badgeUIConfig?.titleTextColor}
          handleChange={(event) =>
            updateBadgeUI(event, profileIndex, "titleTextColor", null, isOrganization)
          }
        />
      </div>
      <div className="flex justify-between my-2">
        <h2 className="">Caption Colour</h2>
        <ColorPicker
          color={profile.badgeUIConfig?.captionTextColor}
          handleChange={(event) =>
            updateBadgeUI(event, profileIndex, "captionTextColor", null, isOrganization)
          }
        />
      </div>
      <div className="flex justify-between my-2">
        <h2 className="">Ring Gradient Color</h2>
        <div className="grid grid-cols-2 gap-1">
          <ColorPicker
            color={profile.badgeUIConfig?.ringGradientColor?.colorFrom}
            handleChange={(event) =>
              updateBadgeUI(event, profileIndex, "ringGradientColor", "colorFrom", isOrganization)
            }
          />
          <ColorPicker
            color={profile.badgeUIConfig?.ringGradientColor?.colorTo}
            handleChange={(event) =>
              updateBadgeUI(event, profileIndex, "ringGradientColor", "colorTo", isOrganization)
            }
          />
        </div>
      </div>

      {/* Caption Field Configuration */}
      {updateCaptionFieldConfig && (
        <div className="mt-6 border-t pt-4">
          <div className="flex justify-between items-center my-2">
            <h2 className="font-medium">Caption Fields</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {(profile.badgeUIConfig?.captionFieldKeys || []).length}/{MAX_CAPTION_FIELDS}
              </span>
              <button
                type="button"
                disabled={
                  (profile.badgeUIConfig?.captionFieldKeys || []).length >= MAX_CAPTION_FIELDS
                }
                className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm ${
                  (profile.badgeUIConfig?.captionFieldKeys || []).length >= MAX_CAPTION_FIELDS
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
                onClick={() => {
                  const currentFields = profile.badgeUIConfig?.captionFieldKeys || [];
                  if (currentFields.length < MAX_CAPTION_FIELDS) {
                    const newFields = [...currentFields, null];
                    updateCaptionFieldConfig(
                      "captionFieldKeys",
                      newFields,
                      profileIndex,
                      isOrganization
                    );
                  }
                }}>
                <PlusIcon className="w-4 h-4" />
                Add Field
              </button>
            </div>
          </div>

          {/* Field Type Information */}
          <div className="mb-3 p-2 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-700">
              <strong>Allowed field types:</strong> {Object.values(FIELD_TYPE_LABELS).join(", ")}
            </p>
          </div>

          {/* Dynamic Field Dropdowns */}
          <div className="space-y-3">
            {(profile.badgeUIConfig?.captionFieldKeys || []).map((selectedField, fieldIndex) => (
              <div key={fieldIndex} className="flex items-center gap-3">
                <div className="flex-1">
                  <InputSelect
                    onChange={(newSelectedField) => {
                      const currentFields = [...(profile.badgeUIConfig?.captionFieldKeys || [])];
                      currentFields[fieldIndex] = newSelectedField;
                      updateCaptionFieldConfig(
                        "captionFieldKeys",
                        currentFields,
                        profileIndex,
                        isOrganization
                      );
                    }}
                    value={
                      (selectedField &&
                        availableFields.find((field) => field._id === selectedField._id)) ||
                      null
                    }
                    subheading={`Field ${fieldIndex + 1}`}
                    className="w-full !mb-0"
                    options={[
                      { label: "Select a field...", value: null, _id: null },
                      ...availableFields
                    ]}
                  />
                </div>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 bg-red-500 text-white rounded-md hover:bg-red-600"
                  onClick={() => {
                    const currentFields = [...(profile.badgeUIConfig?.captionFieldKeys || [])];
                    currentFields.splice(fieldIndex, 1);
                    updateCaptionFieldConfig(
                      "captionFieldKeys",
                      currentFields.length > 0 ? currentFields : null,
                      profileIndex,
                      isOrganization
                    );
                  }}>
                  <span className="text-sm">×</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profession Sub Fields Configuration */}
      {updateCaptionFieldConfig && (
        <div className="mt-6 border-t pt-4">
          <div className="flex justify-between items-center my-2">
            <h2 className="font-medium">Profession Sub Fields</h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {(profile.badgeUIConfig?.professionSubFields || []).length}/
                {MAX_PROFESSION_SUB_FIELDS}
              </span>
              <button
                type="button"
                disabled={
                  (profile.badgeUIConfig?.professionSubFields || []).length >=
                  MAX_PROFESSION_SUB_FIELDS
                }
                className={`flex items-center gap-2 px-3 py-1 rounded-md text-sm ${
                  (profile.badgeUIConfig?.professionSubFields || []).length >=
                  MAX_PROFESSION_SUB_FIELDS
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-500 text-white hover:bg-green-600"
                }`}
                onClick={() => {
                  const currentFields = profile.badgeUIConfig?.professionSubFields || [];
                  if (currentFields.length < MAX_PROFESSION_SUB_FIELDS) {
                    const newFields = [...currentFields, null];
                    updateCaptionFieldConfig(
                      "professionSubFields",
                      newFields,
                      profileIndex,
                      isOrganization
                    );
                  }
                }}>
                <PlusIcon className="w-4 h-4" />
                Add Field
              </button>
            </div>
          </div>

          {/* Field Type Information */}
          <div className="mb-3 p-2 bg-green-50 rounded-md">
            <p className="text-sm text-green-700">
              <strong>Available profession sub fields:</strong>{" "}
              {PROFESSION_SUB_FIELD_OPTIONS.map((option) => option.label).join(", ")}
            </p>
          </div>

          {/* Dynamic Field Dropdowns */}
          <div className="space-y-3">
            {(profile.badgeUIConfig?.professionSubFields || []).map((selectedField, fieldIndex) => (
              <div key={fieldIndex} className="flex items-center gap-3">
                <div className="flex-1">
                  <InputSelect
                    onChange={(newSelectedField) => {
                      const currentFields = [...(profile.badgeUIConfig?.professionSubFields || [])];
                      currentFields[fieldIndex] = newSelectedField?.value || null;
                      updateCaptionFieldConfig(
                        "professionSubFields",
                        currentFields,
                        profileIndex,
                        isOrganization
                      );
                    }}
                    value={
                      selectedField
                        ? PROFESSION_SUB_FIELD_OPTIONS.find(
                            (option) => option.value === selectedField
                          ) || null
                        : null
                    }
                    subheading={`Profession Sub Field ${fieldIndex + 1}`}
                    className="w-full !mb-0"
                    options={[
                      { label: "Select a profession sub field...", value: null, _id: null },
                      ...PROFESSION_SUB_FIELD_OPTIONS
                    ]}
                  />
                </div>
                <button
                  type="button"
                  className="flex items-center justify-center w-8 h-8 bg-red-500 text-white rounded-md hover:bg-red-600"
                  onClick={() => {
                    const currentFields = [...(profile.badgeUIConfig?.professionSubFields || [])];
                    currentFields.splice(fieldIndex, 1);
                    updateCaptionFieldConfig(
                      "professionSubFields",
                      currentFields.length > 0 ? currentFields : null,
                      profileIndex,
                      isOrganization
                    );
                  }}>
                  <span className="text-sm">×</span>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4">
        <ImagePicker
          selectImage={handleImageSelect}
          profileIndex={profileIndex}
          isOrganization={isOrganization}
          selectedImage={profile.badgeUIConfig?.cornerImageObj}
        />
      </div>
      <div className="my-5">
        <ProfileCard
          title="Card Title"
          caption={
            profile.badgeUIConfig?.captionFieldKeys &&
            Array.isArray(profile.badgeUIConfig.captionFieldKeys) &&
            profile.badgeUIConfig.captionFieldKeys.length > 0 ? (
              <div>
                <div>Card Caption</div>
                {profile.badgeUIConfig.captionFieldKeys.map((field, index) =>
                  field && field.label ? (
                    <div key={field._id || index} className="text-sm">
                      [{field.label}]
                    </div>
                  ) : null
                )}
              </div>
            ) : (
              "Card Caption"
            )
          }
          gradient={profile.badgeUIConfig?.ringGradientColor}
          primaryColor={profile.badgeUIConfig?.primaryColor}
          titleTextColor={profile.badgeUIConfig?.titleTextColor}
          secondaryColor={profile.badgeUIConfig?.secondaryColor}
          captionTextColor={profile.badgeUIConfig?.captionTextColor}
          imageUrl="https://placehold.co/450x450/png"
          secondaryImage={
            profile.badgeUIConfig?.cornerImageObj
              ? URL.createObjectURL(profile.badgeUIConfig?.cornerImageObj)
              : profile.badgeUIConfig?.secondaryImage
                ? process.env.REACT_APP_AWS_URL + profile.badgeUIConfig?.secondaryImage
                : "https://placehold.co/60x60/png"
          }
        />
      </div>
    </>
  );
};

const ImagePicker = ({ selectImage, profileIndex, isOrganization, selectedImage }) => {
  const fileRefs = useRef({});
  return (
    <>
      <div className="flex items-baseline justify-between">
        <h3>Corner image</h3>
        <button
          className="underline text-red-500"
          onClick={() => selectImage(null, profileIndex, isOrganization, true)}>
          Remove Image
        </button>
      </div>
      <input
        ref={(el) => (fileRefs.current.img = el)}
        name="image_de"
        type="file"
        accept="image/*"
        onChange={(event) => {
          selectImage(event.currentTarget.files[0], profileIndex, isOrganization);
        }}
        className="hidden"
      />
      <div
        className="ml-3 mb-2 flex gap-4 mt-2 px-2 py-3 rounded-3xl  placeholder-[#606060] bg-[#F6F5F5] text-sm shadow-md max-w-60 cursor-pointer"
        onClick={() => {
          fileRefs.current.img.click();
        }}>
        <PlusSmallIcon className={`w-5 h-5 stroke-1 stroke-white bg-gray-400 rounded-full`} />
        {selectedImage && "Save to Upload"}
      </div>
    </>
  );
};
